import React, { useState, useEffect } from 'react';
import ScoreCard from '../../components/risk/ScoreCard';
import ScopeAssignModal from '../../components/risk/ScopeAssignModal';
import { RiskCard, RiskCardHeader, RiskCardBody, RiskProgress } from '../../components/risk/UiKit';
import ExceptionsModal from '../../components/risk/ExceptionsModal';
import BoardPack from './BoardPack';
import QuestionScoresView from '../../components/risk/QuestionScoresView';
import EvidenceUploadModal from '../../components/risk/EvidenceUploadModal';
import EvidenceDisplay from '../../components/risk/EvidenceDisplay';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

// Tooltip component for explanations
const Tooltip = ({ content, children }) => {
  const [show, setShow] = useState(false);

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        className="cursor-help flex items-center gap-1"
      >
        {children}
        <span className="text-blue-500 text-xs font-bold">ⓘ</span>
      </div>
      {show && (
        <div className="absolute z-50 max-w-xs p-3 mt-2 text-sm text-white bg-gray-900 rounded-lg shadow-xl border border-gray-700 left-1/2 transform -translate-x-1/2">
          <div className="whitespace-pre-line break-words">{content}</div>
          <div className="absolute w-2 h-2 bg-gray-900 transform rotate-45 -top-1 left-1/2 -translate-x-1/2 border-l border-t border-gray-700"></div>
        </div>
      )}
    </div>
  );
};

const API_BASE = process.env.REACT_APP_API_URL + '/api';

// Expandable Risk Card Component with Question Analysis
const ExpandableRiskCard = ({ score, rank, onFlag, assessmentId }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [questionScores, setQuestionScores] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchQuestionScores = async () => {
    if (questionScores.length > 0) return; // Already loaded
    
    setLoading(true);
    let questionsFound = [];
    
    try {
      console.log('🔍 Score object:', score);
      console.log('🔍 Assessment ID:', assessmentId);
      console.log('🔍 Department ID from score:', score.department_id);
      console.log('🔍 FDR ID from score:', score.fdr_id);
      
      // Get question scores from the API with department/FDR filtering
      const url = new URL(`${API_BASE}/risk-assessment/assessments/${assessmentId}/question-scores`);
      if (score.department_id) url.searchParams.set('department_id', score.department_id);
      if (score.fdr_id) url.searchParams.set('fdr_id', score.fdr_id);
      
      console.log('🔍 Fetching question scores from:', url.toString());
      
      const res = await fetch(url.toString(), {
        credentials: 'include'
      });
      const data = await res.json();
      
      if (data.success && data.questionScores) {
        console.log('🔍 FULL API RESPONSE:', data);
        console.log('🔍 Number of domains returned:', data.questionScores.length);
        
        // Find the specific domain
        const targetDomain = data.questionScores.find(domain => domain.domain_name === score.domain_name);
        
        if (targetDomain && targetDomain.questions) {
          console.log('🔍 Target domain found:', targetDomain.domain_name);
          console.log('🔍 Questions in target domain:', targetDomain.questions.length);
          console.log('🔍 Sample questions:', targetDomain.questions.slice(0, 3));
          
          // Check for duplicates by question_id
          const questionIds = targetDomain.questions.map(q => q.question_id);
          const uniqueQuestionIds = [...new Set(questionIds)];
          console.log('🔍 Total questions:', questionIds.length);
          console.log('🔍 Unique question IDs:', uniqueQuestionIds.length);
          console.log('🔍 Duplicate question IDs:', questionIds.filter((id, index) => questionIds.indexOf(id) !== index));
          
          // Backend already filtered by department/FDR, so no client-side filtering needed
          questionsFound = targetDomain.questions;
        } else {
          console.log('🔍 No target domain found for:', score.domain_name);
          console.log('🔍 Available domains:', data.questionScores.map(d => d.domain_name));
        }
      } else {
        console.log('🔍 API call failed or no data:', data);
      }
      
      setQuestionScores(questionsFound);
    } catch (err) {
      console.error('Error loading question scores:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = () => {
    if (!isExpanded) {
      fetchQuestionScores();
    }
    setIsExpanded(!isExpanded);
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'A': return 'text-red-600 bg-red-50 border-red-200';
      case 'B': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'C': return 'text-green-600 bg-green-50 border-green-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getFlagTooltip = (flag) => {
    const flagTooltips = {
      'high-risk': 'High Risk: This risk has been classified as Tier A (≥50 points) and requires a Corrective Action Plan (CAP).',
      'medium-risk': 'Medium Risk: This risk has been classified as Tier B (30-49 points) and requires a monitoring plan.',
      'low-risk': 'Low Risk: This risk has been classified as Tier C (<30 points) and can be accepted with rationale.',
      'high-severity': 'High Severity: The potential impact of this risk is rated 4 or 5 out of 5, indicating significant potential harm.',
      'high-likelihood': 'High Likelihood: The probability of this risk occurring is rated 4 or 5 out of 5, indicating it is very likely to happen.',
      'high-exposure': 'High Exposure: The scope of potential impact is rated 4 or 5 out of 5, indicating organization-wide effects.',
      'weak-controls': 'Weak Controls: The control strength is below 50%, indicating insufficient risk mitigation.',
      'poor-control-design': 'Poor Control Design: The control design effectiveness is rated below 3 out of 4, indicating inadequate control architecture.',
      'poor-control-operation': 'Poor Control Operation: The control operation effectiveness is rated below 3 out of 4, indicating controls are not functioning properly.'
    };
    return flagTooltips[flag] || `Risk Flag: ${flag}`;
  };

  const getRiskBarColor = (value, max = 100) => {
    const percentage = (value / max) * 100;
    if (percentage >= 75) return 'bg-red-500';
    if (percentage >= 50) return 'bg-yellow-500';
    if (percentage >= 25) return 'bg-blue-500';
    return 'bg-green-500';
  };

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm hover:shadow-md transition-all duration-300">
      {/* Main Risk Card */}
      <div 
        className="p-6 cursor-pointer hover:bg-slate-50 transition-colors"
        onClick={handleToggle}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button className="p-2 hover:bg-slate-200 rounded-lg transition-colors">
              {isExpanded ? (
                <ChevronDownIcon className="w-5 h-5 text-slate-600" />
              ) : (
                <ChevronRightIcon className="w-5 h-5 text-slate-600" />
              )}
            </button>
            
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-lg">
                {rank}
              </div>
              
              <div>
                <h4 className="text-lg font-semibold text-slate-900 mb-1">
                  {score.domain_name}
                </h4>
                <p className="text-sm text-slate-600">
                  {score.department_name} • {score.fdr_name || 'Department Risk'}
                </p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-6">
            <div className="text-right">
              <div className="text-2xl font-bold text-slate-900">
                <Tooltip content="Residual Risk = Inherent Risk × (1 - Control Strength)\n\nThis is the remaining risk after controls are applied. Lower values indicate better risk mitigation through effective controls.">
                  {score.residual_risk}
                </Tooltip>
              </div>
              <div className="text-sm text-slate-500">Residual Risk</div>
            </div>
            
            <div className="text-right">
              <div className="text-lg font-semibold text-slate-700">
                <Tooltip content={`Tier ${score.tier} Risk Classification:\n\n• Tier A (≥50 points): High Risk - CAP Required\n• Tier B (30-49 points): Medium Risk - Monitoring Plan\n• Tier C (<30 points): Low Risk - Accept with Rationale`}>
                  {score.tier}
                </Tooltip>
              </div>
              <div className="text-sm text-slate-500">Tier</div>
            </div>
            
            <div className="flex gap-2">
              <Tooltip content="Flag Exception: Request an exception for this risk, requiring approval from a risk administrator.\n\nUse this when you believe the risk should be accepted despite exceeding normal thresholds, or when special circumstances apply.">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onFlag(score);
                  }}
                  className="px-3 py-1 bg-red-100 hover:bg-red-200 text-red-700 text-sm font-medium rounded-lg transition-colors"
                >
                  Flag Exception
                </button>
              </Tooltip>
            </div>
          </div>
        </div>
      </div>
      
      {/* Expanded Question Analysis */}
      {isExpanded && (
        <div className="border-t border-slate-200 bg-slate-50 p-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-slate-600">Loading question analysis...</span>
            </div>
          ) : questionScores.length > 0 ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h5 className="text-lg font-semibold text-slate-900">
                  Question Analysis for {score.domain_name}
                </h5>
                <div className="text-sm text-slate-600">
                  {questionScores.length} questions analyzed
                </div>
              </div>
              
              {/* Debug info - remove in production */}
              {process.env.NODE_ENV === 'development' && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-xs">
                  <div className="font-medium text-yellow-800 mb-2">Debug Info:</div>
                  <div className="text-yellow-700">
                    <div>Domain: {score.domain_name}</div>
                    <div>Department: {score.department_name}</div>
                    <div>FDR: {score.fdr_name || 'N/A'}</div>
                    <div>Questions loaded: {questionScores.length}</div>
                    {questionScores.length > 0 && (
                      <div className="mt-2">
                        <div className="font-medium">First question fields:</div>
                        <div className="text-xs">
                          {Object.keys(questionScores[0] || {}).join(', ')}
                        </div>
                      </div>
                    )}
                    <details className="mt-2">
                      <summary className="cursor-pointer">Raw data</summary>
                      <pre className="mt-2 text-xs overflow-auto max-h-32">
                        {JSON.stringify(questionScores, null, 2)}
                      </pre>
                    </details>
                  </div>
                </div>
              )}
              
              <div className="grid gap-4">
                {questionScores.map((question, index) => (
                  <div key={question.id || index} className="bg-white rounded-lg border border-slate-200 p-4 hover:shadow-sm transition-shadow">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h6 className="font-medium text-slate-900 mb-2 line-clamp-2">
                          {question.question_text || 'Question text not available'}
                        </h6>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getTierColor(question.tier)}`}>
                            Tier {question.tier || 'C'} - {question.tier_info?.label || 'Risk'}
                          </span>
                          {question.flags && question.flags.length > 0 && (
                            <div className="flex gap-1">
                              {question.flags.slice(0, 3).map((flag, idx) => (
                                <Tooltip key={idx} content={getFlagTooltip(flag)}>
                                  <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full cursor-help">
                                    {flag}
                                  </span>
                                </Tooltip>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="text-right ml-4">
                        <div className="text-xl font-bold text-slate-900">
                          <Tooltip content="Residual Risk = Inherent Risk × (1 - Control Strength)\n\nThis is the remaining risk after controls are applied. Lower values indicate better risk mitigation through effective controls.">
                            {question.residual_risk !== null && question.residual_risk !== undefined ? question.residual_risk : 'N/A'}
                          </Tooltip>
                        </div>
                        <div className="text-xs text-slate-500">Residual Risk</div>
                      </div>
                    </div>
                    
                    {/* Risk Scores Visualization */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-600">
                            <Tooltip content="Severity: The potential impact or damage if this risk occurs.\n\nScale: 1 (Minimal) to 5 (Critical)\nHigher severity = greater potential harm to the organization.">
                              Severity
                            </Tooltip>
                          </span>
                          <span className="font-medium">{question.severity_score !== null && question.severity_score !== undefined ? question.severity_score : (question.severity !== null && question.severity !== undefined ? question.severity : 'N/A')}/5</span>
                        </div>
                        <div className="w-full bg-slate-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${getRiskBarColor(question.severity_score || question.severity || 0, 5)}`}
                            style={{ width: `${((question.severity_score || question.severity || 0) / 5) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-600">
                            <Tooltip content="Likelihood: The probability that this risk will occur.\n\nScale: 1 (Very Unlikely) to 5 (Almost Certain)\nHigher likelihood = more probable the risk will happen.">
                              Likelihood
                            </Tooltip>
                          </span>
                          <span className="font-medium">{question.likelihood_score !== null && question.likelihood_score !== undefined ? question.likelihood_score : (question.likelihood !== null && question.likelihood !== undefined ? question.likelihood : 'N/A')}/5</span>
                        </div>
                        <div className="w-full bg-slate-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${getRiskBarColor(question.likelihood_score || question.likelihood || 0, 5)}`}
                            style={{ width: `${((question.likelihood_score || question.likelihood || 0) / 5) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-600">
                            <Tooltip content="Exposure: The extent of potential impact across the organization.\n\nScale: 1 (Limited Scope) to 5 (Organization-Wide)\nHigher exposure = broader impact across departments/systems.">
                              Exposure
                            </Tooltip>
                          </span>
                          <span className="font-medium">{question.exposure_score !== null && question.exposure_score !== undefined ? question.exposure_score : (question.exposure !== null && question.exposure !== undefined ? question.exposure : 'N/A')}/5</span>
                        </div>
                        <div className="w-full bg-slate-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${getRiskBarColor(question.exposure_score || question.exposure || 0, 5)}`}
                            style={{ width: `${((question.exposure_score || question.exposure || 0) / 5) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Control Scores */}
                    {(question.control_design_score !== null && question.control_design_score !== undefined) || (question.control_operating_score !== null && question.control_operating_score !== undefined) || (question.control_design !== null && question.control_design !== undefined) || (question.control_operating !== null && question.control_operating !== undefined) ? (
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4 pt-4 border-t border-slate-200">
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-600">
                              <Tooltip content="Control Design: How well the control is designed to prevent or detect the risk.\n\nScale: 1 (Poor Design) to 4 (Excellent Design)\nAssesses the control's architecture, completeness, and appropriateness.">
                                Control Design
                              </Tooltip>
                            </span>
                            <span className="font-medium">{question.control_design_score !== null && question.control_design_score !== undefined ? question.control_design_score : (question.control_design !== null && question.control_design !== undefined ? question.control_design : 'N/A')}/4</span>
                          </div>
                          <div className="w-full bg-slate-200 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full ${getRiskBarColor(question.control_design_score || question.control_design || 0, 4)}`}
                              style={{ width: `${((question.control_design_score || question.control_design || 0) / 4) * 100}%` }}
                            ></div>
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-600">
                              <Tooltip content="Control Operation: How effectively the control is functioning in practice.\n\nScale: 1 (Poor Operation) to 4 (Excellent Operation)\nAssesses whether controls are working as intended and being followed.">
                                Control Operation
                              </Tooltip>
                            </span>
                            <span className="font-medium">{question.control_operating_score !== null && question.control_operating_score !== undefined ? question.control_operating_score : (question.control_operating !== null && question.control_operating !== undefined ? question.control_operating : 'N/A')}/4</span>
                          </div>
                          <div className="w-full bg-slate-200 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full ${getRiskBarColor(question.control_operating_score || question.control_operating || 0, 4)}`}
                              style={{ width: `${((question.control_operating_score || question.control_operating || 0) / 4) * 100}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    ) : null}
                    
                    {/* Response Details */}
                    <div className="mt-4 pt-4 border-t border-slate-200">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="font-medium text-slate-600">Response:</span>
                          <span className="ml-2 text-slate-900">{question.response_value || 'No response'}</span>
                        </div>
                        <div>
                          <span className="font-medium text-slate-600">
                            <Tooltip content="Control Strength: The effectiveness of controls in mitigating risk.\n\nFormula: (Control Design + Control Operation) / 2 / 4 × 100%\nRange: 0% (No Control) to 100% (Perfect Control)\nCapped at 100% to prevent unrealistic values.">
                              Control Strength:
                            </Tooltip>
                          </span>
                          <span className="ml-2 text-slate-900">{question.control_strength !== null && question.control_strength !== undefined ? `${question.control_strength}%` : 'N/A'}</span>
                        </div>
                        <div>
                          <span className="font-medium text-slate-600">
                            <Tooltip content="Inherent Risk: The level of risk before any controls or mitigation efforts are applied.\n\nFormula: (Severity × Likelihood × Exposure) / 3\nThis represents the 'raw' risk level without considering existing controls.">
                              Inherent Risk:
                            </Tooltip>
                          </span>
                          <span className="ml-2 text-slate-900">{question.inherent_risk !== null && question.inherent_risk !== undefined ? question.inherent_risk : 'N/A'}</span>
                        </div>
                        <div>
                          <span className="font-medium text-slate-600">
                            <Tooltip content="Calculated: The date and time when this risk assessment was last calculated or updated.\n\nThis timestamp shows when the risk scores, tiers, and metrics were computed based on the current responses and control evaluations.">
                              Calculated:
                            </Tooltip>
                          </span>
                          <span className="ml-2 text-slate-900">{question.updated_at ? new Date(question.updated_at).toLocaleDateString() : 'N/A'}</span>
                        </div>
                        {question.comments && (
                          <div className="lg:col-span-2">
                            <span className="font-medium text-slate-600">Comments:</span>
                            <div className="mt-1 p-2 bg-slate-100 rounded text-slate-900">
                              {question.comments}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <p>No question analysis available for this domain</p>
              <div className="mt-4 text-xs text-slate-400">
                <p>Domain: {score.domain_name}</p>
                <p>Department: {score.department_name}</p>
                <p>FDR: {score.fdr_name || 'N/A'}</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default function AssessmentDetail({ assessmentId, onBack, initialAssignmentHint = null }) {
  const id = assessmentId;
  const [assessment, setAssessment] = useState(null);
  const [scores, setScores] = useState([]);
  const [heatMap, setHeatMap] = useState(null);
  const [completeness, setCompleteness] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [showScopeModal, setShowScopeModal] = useState(false);
  const [myAssignments, setMyAssignments] = useState([]);
  const [activeAssignment, setActiveAssignment] = useState(null);
  const [surveyView, setSurveyView] = useState([]); // domains → sections → questions
  const [savingQuestionId, setSavingQuestionId] = useState(null);
  const [deptProgress, setDeptProgress] = useState([]);
  const [showExceptions, setShowExceptions] = useState(false);
  const [flagContext, setFlagContext] = useState(null); // {domain_id, department_id}
  const [showBoardPack, setShowBoardPack] = useState(false);
  
  // Configurable approval system state
  const [orgSettings, setOrgSettings] = useState(null);
  const [evidence, setEvidence] = useState([]);
  const [showEvidenceModal, setShowEvidenceModal] = useState(false);
  
  // Debounced saver for reliability
  const saveTimers = React.useRef({});
  const scheduleSave = (question, payload, delay = 300) => {
    const key = String(question.id);
    if (saveTimers.current[key]) clearTimeout(saveTimers.current[key]);
    saveTimers.current[key] = setTimeout(() => {
      // Get the current complete response for this question
      const currentResponse = question.response || {};
      const completePayload = { ...currentResponse, ...payload };
      console.log('💾 SAVING COMPLETE RESPONSE:', { questionId: question.id, payload: completePayload });
      saveResponse(question, completePayload);
      delete saveTimers.current[key];
    }, delay);
  };

  // Immediate UI update function
  const updateUI = (question, payload) => {
    console.log('🔄 UPDATEUI - Question:', question.id, 'Payload:', payload);
    setSurveyView(prev => {
      const updated = prev.map(d => ({
        ...d,
        sections: d.sections.map(s => ({
          ...s,
          questions: s.questions.map(q => {
            if (q.id === question.id) {
              const currentResponse = q.response || {};
              const newResponse = { ...currentResponse, ...payload };
              console.log('🔄 UPDATEUI - Current response:', currentResponse, 'New response:', newResponse);
              return { ...q, response: newResponse };
            }
            return q;
          })
        }))
      }));
      console.log('🔄 UPDATEUI - Updated survey view:', updated);
      return updated;
    });
  };

  // Lightweight tooltip component
  const Info = ({ text }) => (
    <span className="ml-1 text-slate-400 cursor-help" title={text}>ℹ️</span>
  );

  useEffect(() => {
    if (id) {
      fetchAssessmentDetail();
      fetchCompleteness();
    }
  }, [id]);

  const fetchAssessmentDetail = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/risk-assessment/assessments/${id}`, {
        credentials: 'include'
      });
      const data = await res.json();
      if (data.success) {
        setAssessment(data.assessment);
        
        // If compiled, also fetch scores
        if (['compiled', 'approved', 'published'].includes(data.assessment.status)) {
          fetchScores();
        }
        
        // Always fetch organization settings for approval logic
        fetchOrgSettings();
        
        // If approved, also fetch evidence
        if (data.assessment.status === 'approved') {
          fetchEvidence();
        }
      } else {
        setMessage('❌ Failed to load assessment');
      }
    } catch (err) {
      setMessage('❌ Error loading assessment');
      console.error(err);
    }
    setLoading(false);
  };

  const fetchScores = async () => {
    try {
      const res = await fetch(`${API_BASE}/risk-assessment/assessments/${id}/scores`, {
        credentials: 'include'
      });
      const data = await res.json();
      if (data.success) {
        setScores(data.scores);
        setHeatMap(data.heatMap);
      }
    } catch (err) {
      console.error('Error loading scores:', err);
    }
  };

  const fetchCompleteness = async () => {
    try {
      const res = await fetch(`${API_BASE}/risk-assessment/assessments/${id}/completeness`, {
        credentials: 'include'
      });
      const data = await res.json();
      if (data.success) {
        setCompleteness(data.completeness);
      }
    } catch (err) {
      console.error('Error loading completeness:', err);
    }
  };

  const fetchOrgSettings = async () => {
    try {
      const res = await fetch(`${API_BASE}/risk-assessment/organization/settings`, {
        credentials: 'include'
      });
      const data = await res.json();
      console.log('Org settings response:', data);
      if (data.success) {
        setOrgSettings(data.settings);
      } else {
        console.error('Failed to fetch org settings:', data.message);
        // Set default settings if API fails
        setOrgSettings({
          require_ceo_approval: false,
          ceo_approval_method: 'evidence',
          ceo_email: ''
        });
      }
    } catch (err) {
      console.error('Error loading organization settings:', err);
      // Set default settings if API fails
      setOrgSettings({
        require_ceo_approval: false,
        ceo_approval_method: 'evidence',
        ceo_email: ''
      });
    }
  };

  const fetchEvidence = async () => {
    try {
      const res = await fetch(`${API_BASE}/risk-assessment/assessments/${id}/evidence`, {
        credentials: 'include'
      });
      const data = await res.json();
      if (data.success) {
        setEvidence(data.evidence);
      }
    } catch (err) {
      console.error('Error loading evidence:', err);
    }
  };

  // Load assignments when Responses tab opens
  useEffect(() => {
    if (activeTab === 'responses' && id) {
      fetchMyAssignments();
    }
  }, [activeTab, id]);

  useEffect(() => {
    if ((activeTab === 'overview' || activeTab === 'scope' || activeTab === 'responses') && id) {
      fetchDeptProgress();
    }
  }, [activeTab, id]);

  const fetchMyAssignments = async () => {
    try {
      const res = await fetch(`${API_BASE}/risk-assessment/assessments/${id}/my-assignments`, {
        credentials: 'include'
      });
      const data = await res.json();
      if (data.success) {
        console.log('🔍 ASSIGNMENTS LOADED:', data.assignments);
        setMyAssignments(data.assignments || []);
        if (!activeAssignment && (data.assignments || []).length > 0) {
          // Try to focus the assignment hinted by notifications
          const hint = initialAssignmentHint;
          const preferred = hint
            ? data.assignments.find(a => (hint.department_id && a.department_id === hint.department_id) || (hint.fdr_id && a.fdr_id === hint.fdr_id))
            : null;
          console.log('🔍 SELECTING ASSIGNMENT:', preferred || data.assignments[0]);
          handleSelectAssignment(preferred || data.assignments[0]);
        }
      }
    } catch (err) {
      console.error('Error loading assignments:', err);
    }
  };

  const fetchDeptProgress = async () => {
    try {
      const res = await fetch(`${API_BASE}/risk-assessment/assessments/${id}/progress`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) setDeptProgress(data.items || []);
    } catch (err) {
      console.error('Error loading progress:', err);
    }
  };

  const handleSelectAssignment = async (assignment) => {
    setActiveAssignment(assignment);
    try {
      const params = new URLSearchParams();
      if (assignment.department_id) params.append('department_id', assignment.department_id);
      if (assignment.fdr_id) params.append('fdr_id', assignment.fdr_id);
      const res = await fetch(`${API_BASE}/risk-assessment/assessments/${id}/survey?${params.toString()}`, {
        credentials: 'include'
      });
      const data = await res.json();
      console.log('🔍 SURVEY LOAD RESPONSE:', data);
      if (data.success) {
        console.log('🔍 SURVEY DATA:', data.survey);
        console.log('🔍 SURVEY DATA STRUCTURE:', JSON.stringify(data.survey, null, 2));
        setSurveyView(data.survey || []);
      } else {
        console.log('❌ SURVEY LOAD FAILED:', data);
        setSurveyView([]);
      }
    } catch (err) {
      console.error('Error loading survey:', err);
    }
  };

  const parseOptions = (opts) => {
    if (!opts) return [];
    if (Array.isArray(opts)) return opts;
    try { const parsed = JSON.parse(opts); return Array.isArray(parsed) ? parsed : []; } catch { return []; }
  };

  const saveResponse = async (question, payload) => {
    setSavingQuestionId(question.id);
    try {
      const base = {
        assessment_id: id,
        question_id: question.id,
        department_id: activeAssignment?.department_id || null,
        fdr_id: activeAssignment?.fdr_id || null
      };
      const res = await fetch(`${API_BASE}/risk-assessment/responses`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...base, ...payload })
      });
      const json = await res.json();
      if (!json.success) {
        setMessage(`❌ ${json.message || 'Failed to save response'}`);
        setTimeout(() => setMessage(''), 3000);
      } else if (json.response) {
        // Server response received - no need to update UI since updateUI already handled it
        console.log('✅ Response saved successfully:', json.response);
      }
    } catch (err) {
      console.error('Failed to save response:', err);
      setMessage('❌ Network error while saving');
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setSavingQuestionId(null);
    }
  };

  const submitMyArea = async () => {
    if (!activeAssignment) return;
    try {
      const res = await fetch(`${API_BASE}/risk-assessment/assessments/${id}/submit`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ department_id: activeAssignment.department_id || null, fdr_id: activeAssignment.fdr_id || null })
      });
      const json = await res.json();
      if (json.success) {
        setMessage('✅ Submitted to admin');
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage(`❌ ${json.message || 'Submit failed'}`);
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (e) {
      setMessage('❌ Network error while submitting');
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const handleEvidenceAdd = (question, files, currentEvidence = []) => {
    const maxFiles = 5;
    const maxSize = 2 * 1024 * 1024; // 2MB
    const allowed = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
    const toRead = [];
    for (const f of files) {
      if (!allowed.includes(f.type)) {
        alert(`Unsupported file type: ${f.type}`);
        return;
      }
      if (f.size > maxSize) {
        alert(`${f.name} exceeds 2MB limit`);
        return;
      }
      toRead.push(f);
    }

    const readerPromises = toRead.map((file) => new Promise((resolve) => {
      const fr = new FileReader();
      fr.onload = () => resolve({ name: file.name, type: file.type, size: file.size, data: fr.result });
      fr.readAsDataURL(file);
    }));

    Promise.all(readerPromises).then((newItems) => {
      const merged = [...currentEvidence, ...newItems].slice(0, maxFiles);
      saveResponse(question, { evidence_files: merged });
    });
  };

  const handleCompile = async () => {
    if (!window.confirm('Compile this assessment? This will calculate risk scores based on current responses.')) {
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/risk-assessment/assessments/${id}/compile`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await res.json();
      
      if (data.success) {
        setMessage('✅ Assessment compiled successfully');
        fetchAssessmentDetail();
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage(`❌ ${data.message}`);
      }
    } catch (err) {
      setMessage('❌ Network error occurred');
      console.error(err);
    }
  };

  const handleStart = async () => {
    if (!window.confirm('Start this assessment and send to respondents? You will no longer be able to edit scope while active.')) return;
    try {
      const res = await fetch(`${API_BASE}/risk-assessment/assessments/${id}/start`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (data.success) {
        setMessage('✅ Assessment started');
        fetchAssessmentDetail();
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage(`❌ ${data.message || 'Failed to start assessment'}`);
      }
    } catch (err) {
      setMessage('❌ Network error occurred');
      console.error(err);
    }
  };

  const handleApprove = async (approvalType) => {
    const approvalText = approvalType === 'officer_approval' ? 'Officer Approval' : 'CEO/Committee Approval';
    const rationale = window.prompt(`Enter rationale for ${approvalText}:`);
    
    if (!rationale) return;

    try {
      const res = await fetch(`${API_BASE}/risk-assessment/assessments/${id}/approve`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ approval_type: approvalType, rationale })
      });

      const data = await res.json();
      
      if (data.success) {
        setMessage(`✅ ${approvalText} completed`);
        
        // If CEO approval is disabled and this is officer approval, auto-publish
        if (approvalType === 'officer_approval' && orgSettings && !orgSettings.require_ceo_approval) {
          setTimeout(async () => {
            try {
              const publishRes = await fetch(`${API_BASE}/risk-assessment/assessments/${id}/approve`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ approval_type: 'ceo_approval', rationale: 'Auto-published - CEO approval disabled' })
              });
              
              if (publishRes.ok) {
                setMessage('✅ Assessment published automatically');
                fetchAssessmentDetail();
                setTimeout(() => setMessage(''), 3000);
              }
            } catch (err) {
              console.error('Auto-publish failed:', err);
            }
          }, 1000);
        } else {
          fetchAssessmentDetail();
        }
        
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage(`❌ ${data.message}`);
      }
    } catch (err) {
      setMessage('❌ Network error occurred');
      console.error(err);
    }
  };

  const handleEvidenceReview = async (evidenceId, status) => {
    const rationale = window.prompt(`Enter rationale for ${status}:`);
    if (!rationale) return;

    try {
      const res = await fetch(`${API_BASE}/risk-assessment/assessments/${id}/evidence/${evidenceId}/review`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, rationale })
      });

      const data = await res.json();
      
      if (data.success) {
        setMessage(`✅ Evidence ${status}`);
        fetchEvidence();
        fetchAssessmentDetail(); // Refresh to check if assessment can be published
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage(`❌ ${data.message}`);
      }
    } catch (err) {
      setMessage('❌ Network error occurred');
      console.error(err);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Draft' },
      active: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Active' },
      compiled: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Compiled' },
      approved: { bg: 'bg-green-100', text: 'text-green-800', label: 'Approved' },
      published: { bg: 'bg-amber-100', text: 'text-amber-800', label: 'Published' }
    };

    const config = statusConfig[status] || statusConfig.draft;
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  // Role-based UI gating
  const sessionUser = (() => { try { return JSON.parse(localStorage.getItem('sessionUser') || 'null'); } catch { return null; } })();
  const coreRole = sessionUser?.role;
  const riskInfo = sessionUser?.app_roles?.risk;
  const riskRole = riskInfo?.role;
  const riskDeptScoped = Boolean(riskInfo?.department_id);

  const allowBuild = coreRole === 'superadmin' || (riskRole === 'admin' && !riskDeptScoped);
  const allowApprove = coreRole === 'superadmin' || riskRole === 'officer';

  const canCompile = allowBuild && assessment?.status === 'active' && completeness?.canCompile;
  const canApprove = allowApprove && assessment?.status === 'compiled';
  
  // Configurable CEO approval logic
  const requiresCEOApproval = orgSettings?.require_ceo_approval;
  const ceoApprovalMethod = orgSettings?.ceo_approval_method;
  
  // Debug logging
  console.log('AssessmentDetail Debug:', {
    assessmentStatus: assessment?.status,
    orgSettings,
    requiresCEOApproval,
    ceoApprovalMethod,
    evidence: evidence.length
  });
  
  const canPublish = allowApprove && assessment?.status === 'approved' && (
    !requiresCEOApproval || // No CEO approval required
    (requiresCEOApproval && ceoApprovalMethod === 'system') || // System-based CEO approval
    (requiresCEOApproval && ceoApprovalMethod === 'evidence' && evidence.some(e => e.status === 'approved')) // Evidence-based approval
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!assessment) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Assessment Not Found</h2>
          <button
            onClick={onBack}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
          >
            Back to Assessments
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[95vw] mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl p-8 text-white">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={onBack}
            className="text-slate-300 hover:text-white transition-colors"
          >
            ← Back to Assessments
          </button>
          <div className="flex items-center space-x-3">
            {getStatusBadge(assessment.status)}
            {allowBuild && assessment.status === 'draft' && (
              <button
                onClick={() => setShowScopeModal(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Scope & Assign
              </button>
            )}
            {allowBuild && assessment.status === 'draft' && (
              <button
                onClick={handleStart}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Start / Send
              </button>
            )}
            {canCompile && (
              <button
                onClick={handleCompile}
                className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Compile Assessment
              </button>
            )}
            {canApprove && (
              <button
                onClick={() => handleApprove('officer_approval')}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Officer Approval
              </button>
            )}
            
            {/* Configurable CEO Approval */}
            {assessment?.status === 'approved' && requiresCEOApproval && (
              <>
                {ceoApprovalMethod === 'system' && (
                  <button
                    onClick={() => handleApprove('ceo_approval')}
                    className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                  >
                    CEO Approval
                  </button>
                )}
                
                {ceoApprovalMethod === 'evidence' && (
                  <button
                    onClick={() => setShowEvidenceModal(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                  >
                    Upload Evidence
                  </button>
                )}
              </>
            )}
            
            {/* No Final Approval button when CEO approval is disabled - assessment should auto-publish */}
            {['compiled','approved','published'].includes(assessment.status) && (
              <button
                onClick={()=>setShowBoardPack(true)}
                className="bg-slate-700 hover:bg-slate-800 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Board Pack
              </button>
            )}
          </div>
        </div>
        
        <h1 className="text-4xl font-bold mb-3">{assessment.title}</h1>
        <div className="flex items-center space-x-6 text-slate-300">
          <span>Year: {assessment.assessment_year}</span>
          <span>Created by: {assessment.created_by_name}</span>
          <span>Created: {new Date(assessment.created_at).toLocaleDateString()}</span>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-xl text-sm font-medium ${
          message.startsWith('✅') 
            ? 'bg-green-50 text-green-800 border border-green-200' 
            : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {message}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="border-b border-slate-200">
        <nav className="flex space-x-8">
          {[
            { id: 'overview', label: 'Overview' },
            { id: 'scope', label: 'Scope & Assignments' },
            { id: 'responses', label: 'Response Progress' },
            { id: 'scores', label: 'Risk Scores & Analysis', disabled: !['compiled', 'approved', 'published'].includes(assessment.status) }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => !tab.disabled && setActiveTab(tab.id)}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : tab.disabled
                  ? 'border-transparent text-slate-400 cursor-not-allowed'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
              disabled={tab.disabled}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      <div className="space-y-6">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Assessment Info */}
            <div className="lg:col-span-2 space-y-6">
              <RiskCard>
                <RiskCardHeader title="Assessment Information" />
                <RiskCardBody>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Status:</span>
                    <div className="mt-1">{getStatusBadge(assessment.status)}</div>
                  </div>
                  <div>
                    <span className="text-slate-500">Assessment Year:</span>
                    <div className="mt-1 font-medium">{assessment.assessment_year}</div>
                  </div>
                  <div>
                    <span className="text-slate-500">Created:</span>
                    <div className="mt-1 font-medium">{new Date(assessment.created_at).toLocaleDateString()}</div>
                  </div>
                  <div>
                    <span className="text-slate-500">Created by:</span>
                    <div className="mt-1 font-medium">{assessment.created_by_name}</div>
                  </div>
                  {assessment.compiled_at && (
                    <>
                      <div>
                        <span className="text-slate-500">Compiled:</span>
                        <div className="mt-1 font-medium">{new Date(assessment.compiled_at).toLocaleDateString()}</div>
                      </div>
                    </>
                  )}
                  {assessment.approved_at && (
                    <>
                      <div>
                        <span className="text-slate-500">Approved:</span>
                        <div className="mt-1 font-medium">{new Date(assessment.approved_at).toLocaleDateString()}</div>
                      </div>
                    </>
                  )}
                </div>
                </RiskCardBody>
              </RiskCard>

              {/* Scope Summary */}
              <RiskCard>
                <RiskCardHeader title="Assessment Scope" />
                <RiskCardBody>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium text-slate-900 mb-2">Departments ({assessment.departments?.length || 0})</h4>
                    <div className="space-y-1">
                      {assessment.departments?.slice(0, 5).map((dept) => (
                        <div key={dept.department_id} className="text-sm text-slate-600">
                          {dept.department_name}
                        </div>
                      ))}
                      {(assessment.departments?.length || 0) > 5 && (
                        <div className="text-sm text-slate-500">
                          +{(assessment.departments?.length || 0) - 5} more...
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium text-slate-900 mb-2">FDRs ({assessment.fdrs?.length || 0})</h4>
                    <div className="space-y-1">
                      {assessment.fdrs?.slice(0, 5).map((fdr) => (
                        <div key={fdr.id} className="text-sm text-slate-600">
                          {fdr.fdr_name}
                        </div>
                      ))}
                      {(assessment.fdrs?.length || 0) > 5 && (
                        <div className="text-sm text-slate-500">
                          +{(assessment.fdrs?.length || 0) - 5} more...
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium text-slate-900 mb-2">Department Progress</h4>
                    <div className="space-y-2">
                      {deptProgress.slice(0, 5).map((p) => (
                        <div key={p.department_id} className="text-sm">
                          <div className="flex items-center justify-between">
                            <span className="text-slate-700 truncate mr-3">{p.department_name}</span>
                            <span className="text-slate-600">{p.percent}%</span>
                          </div>
                          <RiskProgress value={p.percent} />
                        </div>
                      ))}
                      {deptProgress.length > 5 && (
                        <div className="text-xs text-slate-500">+{deptProgress.length - 5} more…</div>
                      )}
                    </div>
                  </div>
                </div>
                </RiskCardBody>
              </RiskCard>
            </div>

            {/* Progress Card */}
            <div className="space-y-6">
              {completeness && (
                <RiskCard>
                  <RiskCardHeader title="Progress" />
                  <RiskCardBody>
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>Overall Completion</span>
                        <span>{completeness.completionPercentage}%</span>
                      </div>
                      <RiskProgress value={completeness.completionPercentage} />
                    </div>
                    
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>Risk Scoring</span>
                        <span>{completeness.riskScoringPercentage}%</span>
                      </div>
                      <RiskProgress value={completeness.riskScoringPercentage} />
                    </div>

                    <div className="pt-2 border-t border-slate-200">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-slate-500">Completed:</span>
                          <div className="font-medium">{completeness.completed}/{completeness.totalRequired}</div>
                        </div>
                        <div>
                          <span className="text-slate-500">With Risk Scores:</span>
                          <div className="font-medium">{completeness.withRiskScores}</div>
                        </div>
                      </div>
                    </div>

                    {completeness.canCompile && assessment.status === 'active' && allowBuild && (
                      <button
                        onClick={handleCompile}
                        className="w-full bg-yellow-600 hover:bg-yellow-700 text-white py-2 px-4 rounded-lg font-medium transition-colors"
                      >
                        Ready to Compile
                      </button>
                    )}
                    {assessment.status === 'active' && (
                      <button
                        onClick={async ()=>{
                          await fetch(`${API_BASE}/risk-assessment/assessments/${id}/reminders`, { method: 'POST', credentials: 'include' });
                          setMessage('✅ Reminders sent');
                          setTimeout(()=>setMessage(''), 2500);
                        }}
                        className="w-full bg-slate-700 hover:bg-slate-800 text-white py-2 px-4 rounded-lg font-medium transition-colors"
                      >
                        Send Reminders
                      </button>
                    )}
                  </div>
                </RiskCardBody>
                </RiskCard>
              )}

              {/* Quick Actions */}
              <RiskCard>
                <RiskCardBody>
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Quick Actions</h3>
                <div className="space-y-3">
                  <button
                    onClick={() => setActiveTab('responses')}
                    className="w-full text-left p-3 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    <div className="font-medium text-slate-900">Respond to Survey</div>
                    <div className="text-sm text-slate-600">Complete your assigned questions</div>
                  </button>
                  
                  <button
                    onClick={() => setActiveTab('scores')}
                    disabled={!['compiled', 'approved', 'published'].includes(assessment.status)}
                    className="w-full text-left p-3 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="font-medium text-slate-900">View Risk Scores</div>
                    <div className="text-sm text-slate-600">See compiled risk analysis</div>
                  </button>
                </div>
                </RiskCardBody>
              </RiskCard>
            </div>
          </div>
        )}

        {activeTab === 'scope' && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">Scope & Assignments</h3>
              {allowBuild && assessment.status === 'draft' && (
                <button
                  onClick={() => setShowScopeModal(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                >
                  Edit Scope & Assignments
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-slate-900 mb-2">Departments</h4>
                <div className="space-y-1">
                  {assessment.departments?.length ? (
                    assessment.departments.map((d) => (
                      <div key={d.department_id} className="text-sm text-slate-700">{d.department_name}</div>
                    ))
                  ) : (
                    <div className="text-sm text-slate-500">No departments in scope yet.</div>
                  )}
                </div>
              </div>
              <div>
                <h4 className="font-medium text-slate-900 mb-2">Assignments</h4>
                <div className="space-y-2">
                  {assessment.assignments?.length ? (
                    assessment.assignments.map((a) => (
                      <div key={a.id} className="text-sm text-slate-700">
                        <span className="font-medium">{a.department_name || a.fdr_name || '—'}:</span> {a.user_name} ({a.user_email})
                      </div>
                    ))
                  ) : (
                    <div className="text-sm text-slate-500">No respondents assigned yet.</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'scores' && heatMap && (
          <div className="space-y-6">
            {/* Heat Map */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Risk Heat Map - Medicare Advantage Optimized</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="text-2xl font-bold text-red-800">{heatMap.tierA.count}</div>
                  <div className="text-sm text-red-600">Tier A - High Risk</div>
                  <div className="text-xs text-red-500">{heatMap.tierA.percentage}%</div>
                  <div className="text-xs text-red-400 mt-1">≥50 points</div>
                </div>
                <div className="text-center p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="text-2xl font-bold text-yellow-800">{heatMap.tierB.count}</div>
                  <div className="text-sm text-yellow-600">Tier B - Medium Risk</div>
                  <div className="text-xs text-yellow-500">{heatMap.tierB.percentage}%</div>
                  <div className="text-xs text-yellow-400 mt-1">30-49 points</div>
                </div>
                <div className="text-center p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="text-2xl font-bold text-green-800">{heatMap.tierC.count}</div>
                  <div className="text-sm text-green-600">Tier C - Low Risk</div>
                  <div className="text-xs text-green-500">{heatMap.tierC.percentage}%</div>
                  <div className="text-xs text-green-400 mt-1">&lt;30 points</div>
                </div>
              </div>
            </div>

            {/* Top 10 Risks with Expandable Question Analysis */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-slate-900">Top 10 Risks</h3>
                <div className="text-sm text-slate-600">
                  Click on any domain to view detailed question analysis
                </div>
              </div>
              <div className="space-y-4">
                {scores.slice(0, 10).map((score, index) => (
                  <ExpandableRiskCard 
                    key={score.id} 
                    score={score} 
                    rank={index + 1} 
                    onFlag={(s)=>{ setFlagContext({ domain_id: s.domain_id, department_id: s.department_id }); setShowExceptions(true); }}
                    assessmentId={id}
                  />
                ))}
              </div>
            </div>
          </div>
        )}


        {activeTab === 'responses' && (
          <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
            {/* Assignments list */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 xl:col-span-1 order-2 xl:order-1">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">My Assignments</h3>
              <div className="space-y-2">
                {myAssignments.length === 0 && (
                  <div className="text-sm text-slate-500">No assignments yet. Ask your admin to assign you to a department.</div>
                )}
                {myAssignments.map((a) => (
                  <button
                    key={a.id}
                    onClick={() => handleSelectAssignment(a)}
                    className={`w-full text-left p-3 border rounded-lg transition-colors ${activeAssignment?.id === a.id ? 'border-blue-300 bg-blue-50' : 'border-slate-200 hover:bg-slate-50'}`}
                  >
                    <div className="font-medium text-slate-900">{a.department_name || a.fdr_name || 'Assignment'}</div>
                    <div className="text-xs text-slate-600">Click to respond</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Modern Survey Table */}
            <div className="xl:col-span-4 space-y-6 order-1 xl:order-2">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                  <h3 className="text-lg font-semibold text-gray-900">Survey Response</h3>
                  <p className="text-sm text-gray-600 mt-1">Complete your risk assessment questionnaire</p>
                </div>

                {!activeAssignment && (
                  <div className="p-8 text-center">
                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <p className="text-gray-600">Select an assignment to begin responding to survey questions</p>
                  </div>
                )}

                {activeAssignment && surveyView.length === 0 && (
                  <div className="p-8 text-center">
                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                      </svg>
                    </div>
                    <p className="text-gray-600">No survey questions available for this assignment</p>
                  </div>
                )}

                {surveyView.length > 0 && (
                  <div className="overflow-x-auto">
                    <div className="xl:hidden mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-800">
                        <span className="font-semibold">Note:</span> This table is optimized for wide screens. You can scroll horizontally to view all columns.
                      </p>
                    </div>
                    <table className="w-full min-w-[1800px] table-fixed border-collapse">
                      <thead className="bg-gradient-to-r from-blue-50 to-blue-100 border-b-2 border-blue-200">
                        <tr>
                          <th className="w-[500px] px-6 py-5 text-left text-sm font-bold text-gray-900 uppercase tracking-wider border-r border-gray-300">
                            Question
                          </th>
                          <th className="w-[500px] px-6 py-5 text-left text-sm font-bold text-gray-900 uppercase tracking-wider border-r border-gray-300">
                            Response
                          </th>
                          <th className="w-[350px] px-6 py-5 text-left text-sm font-bold text-gray-900 uppercase tracking-wider border-r border-gray-300">
                            Risk Scores
                          </th>
                          <th className="w-[300px] px-6 py-5 text-left text-sm font-bold text-gray-900 uppercase tracking-wider border-r border-gray-300">
                            Controls
                          </th>
                          <th className="w-[300px] px-6 py-5 text-left text-sm font-bold text-gray-900 uppercase tracking-wider">
                            Evidence
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white">
                        {surveyView.map((domain) => (
                          <React.Fragment key={domain.id}>
                            {/* Domain Header */}
                            <tr className="bg-blue-600 text-white">
                              <td colSpan={5} className="px-6 py-4 border-b border-gray-300">
                                <div className="font-bold text-base uppercase tracking-wide">{domain.name}</div>
                              </td>
                            </tr>
                            {domain.sections.map((section) => (
                              <React.Fragment key={section.id}>
                                {/* Section Header */}
                                <tr className="bg-gray-100">
                                  <td colSpan={5} className="px-6 py-3 border-b border-gray-300">
                                    <div className="font-semibold text-gray-800 text-base">{section.name}</div>
                                  </td>
                                </tr>
                                {section.questions.map((q) => {
                                  const options = parseOptions(q.options);
                                  const r = q.response || {};
                                  const logSave = (key, val) => {
                                    console.log('🧪 Saving', { qid: q.id, key, val, dept: activeAssignment?.department_id, fdr: activeAssignment?.fdr_id });
                                  };
                                  return (
                                    <tr key={q.id} className="border-b border-gray-300 hover:bg-gray-50 transition-colors">
                                      {/* Question Column */}
                                      <td className="px-6 py-6 align-top border-r border-gray-300">
                                        <div className="font-medium text-gray-900 text-base leading-relaxed pr-4">{q.question_text}</div>
                                        {savingQuestionId === q.id && (
                                          <div className="text-sm text-blue-600 flex items-center mt-3">
                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Saving...
                                          </div>
                                        )}
                                      </td>
                                      
                                      {/* Response Column */}
                                      <td className="px-6 py-6 align-top border-r border-gray-300">
                                        <div className="w-full">
                                          {/* Text Response */}
                                          {q.question_type === 'text' && (
                                            <textarea
                                              value={r.response_value || ''}
                                              onChange={(e) => { 
                                                const value = e.target.value;
                                                updateUI(q, { response_value: value });
                                                scheduleSave(q, { response_value: value });
                                              }}
                                              className="w-full px-4 py-3 border border-gray-300 rounded-lg text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none transition-all duration-200 hover:border-gray-400"
                                              rows={4}
                                              placeholder="Enter your detailed response..."
                                            />
                                          )}
                                          
                                          {/* Select Response */}
                                          {q.question_type === 'select' && (
                                            <select
                                              value={r.response_value || ''}
                                              onChange={(e) => {
                                                const value = e.target.value;
                                                updateUI(q, { response_value: value });
                                                scheduleSave(q, { response_value: value });
                                              }}
                                              className="w-full px-4 py-3 border border-gray-300 rounded-lg text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white transition-all duration-200 hover:border-gray-400"
                                            >
                                              <option value="">Select an option...</option>
                                              {options.map((op) => (
                                                <option key={op.value} value={op.value}>{op.label}</option>
                                              ))}
                                            </select>
                                          )}
                                          
                                          {/* Multi Select Response */}
                                          {q.question_type === 'multi_select' && (
                                            <div className="space-y-3">
                                              {options.map((op) => {
                                                const selected = Array.isArray(r.response_value) ? r.response_value.includes(op.value) : false;
                                                return (
                                                  <label key={op.value} className="flex items-center p-2 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                                                    <input
                                                      type="checkbox"
                                                      checked={selected}
                                                      onChange={(e) => {
                                                        const prev = Array.isArray(r.response_value) ? [...r.response_value] : [];
                                                        if (e.target.checked) prev.push(op.value); else prev.splice(prev.indexOf(op.value), 1);
                                                        updateUI(q, { response_value: prev });
                                                        scheduleSave(q, { response_value: prev });
                                                      }}
                                                      className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-3"
                                                    />
                                                    <span className="text-base text-gray-700">{op.label}</span>
                                                  </label>
                                                );
                                              })}
                                            </div>
                                          )}
                                        </div>
                                      </td>
                                      
                                      {/* Risk Scores Column */}
                                      <td className="px-6 py-6 align-top border-r border-gray-300">
                                        <div className="space-y-5">
                                          {['severity_score','likelihood_score','exposure_score'].map((key) => (
                                            <div key={key} className="bg-gray-50 p-4 rounded-lg">
                                              <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                                                {key.replace('_',' ').replace('score','').replace(/\b\w/g, c => c.toUpperCase())}
                                                {key === 'severity_score' && (
                                                  <Info text={'1 = Minimal impact, 5 = Severe impact to members, operations or compliance.'} />
                                                )}
                                                {key === 'likelihood_score' && (
                                                  <Info text={'1 = Very unlikely to occur, 5 = Very likely to occur this year.'} />
                                                )}
                                                {key === 'exposure_score' && (
                                                  <Info text={'1 = Limited/isolated exposure, 5 = Pervasive/widespread exposure across processes.'} />
                                                )}
                                              </label>
                                              <input
                                                type="number"
                                                min="1" max="5"
                                                value={r[key] ?? ''}
                                                onChange={(e) => { 
                                                  const value = parseInt(e.target.value) || null;
                                                  updateUI(q, { [key]: value });
                                                  scheduleSave(q, { [key]: value });
                                                }}
                                                className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-base text-center focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-gray-400"
                                                placeholder="1-5"
                                              />
                                            </div>
                                          ))}
                                        </div>
                                      </td>
                                      
                                      {/* Controls Column */}
                                      <td className="px-6 py-6 align-top border-r border-gray-300">
                                        {q.control_required && (
                                          <div className="space-y-4">
                                            {['control_design_score','control_operating_score'].map((key) => (
                                              <div key={key} className="bg-blue-50 p-4 rounded-lg">
                                                <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                                                  {key.replace('_',' ').replace('score','').replace(/\b\w/g, c => c.toUpperCase())}
                                                  {key === 'control_design_score' && (
                                                    <Info text={'0 = No control; 1 = Ad‑hoc/undocumented; 2 = Partially documented/inconsistent; 3 = Well‑designed and documented; 4 = Strong, preventive design and independently reviewed.'} />
                                                  )}
                                                  {key === 'control_operating_score' && (
                                                    <Info text={'0 = Not operating; 1 = Rare/ad‑hoc with no evidence; 2 = Inconsistent with some evidence; 3 = Consistent and evidenced; 4 = Mature, monitored and periodically tested.'} />
                                                  )}
                                                </label>
                                                <input
                                                  type="number"
                                                  min="0" max="4"
                                                  value={r[key] ?? ''}
                                                  onChange={(e) => { 
                                                    const value = parseInt(e.target.value) || null;
                                                    updateUI(q, { [key]: value });
                                                    scheduleSave(q, { [key]: value });
                                                  }}
                                                  className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-base text-center focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-gray-400"
                                                  placeholder="0-4"
                                                />
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </td>
                                      
                                      {/* Evidence Column */}
                                      <td className="px-6 py-6 align-top">
                                        <div className="space-y-3">
                                          {Array.isArray(r.evidence_files) && r.evidence_files.length > 0 && (
                                            <div className="space-y-2">
                                              {r.evidence_files.map((ev, idx) => (
                                                <div key={idx} className="flex items-center justify-between text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                                                  <span className="truncate mr-2 flex-1" title={ev.name}>{ev.name || `File ${idx+1}`}</span>
                                                  <div className="flex items-center gap-2">
                                                    {ev.data && (
                                                      <a href={ev.data} target="_blank" rel="noreferrer" className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-100 transition-colors">
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                        </svg>
                                                      </a>
                                                    )}
                                                    <button
                                                      onClick={() => {
                                                        const next = (r.evidence_files || []).filter((_, i) => i !== idx);
                                                        saveResponse(q, { evidence_files: next });
                                                      }}
                                                      className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-100 transition-colors"
                                                    >
                                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                      </svg>
                                                    </button>
                                                  </div>
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                          
                                          {/* Comments Field */}
                                          <div className="space-y-2">
                                            <label className="block text-sm font-medium text-gray-700">Comments</label>
                                            <textarea
                                              value={r.comments || ''}
                                              onChange={(e) => {
                                                const value = e.target.value;
                                                updateUI(q, { comments: value });
                                                scheduleSave(q, { comments: value });
                                              }}
                                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none transition-all duration-200 hover:border-gray-400"
                                              rows={3}
                                              placeholder="Add any additional comments..."
                                            />
                                          </div>
                                          
                                          <input
                                            type="file"
                                            accept=".pdf,image/png,image/jpeg"
                                            multiple
                                            onChange={(e) => {
                                              const files = Array.from(e.target.files || []);
                                              handleEvidenceAdd(q, files, Array.isArray(r.evidence_files) ? r.evidence_files : []);
                                              e.target.value = '';
                                            }}
                                            className="block w-full text-sm text-gray-500 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 transition-colors"
                                          />
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </React.Fragment>
                            ))}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {activeAssignment && surveyView.length > 0 && (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mt-6">
                    <div className="flex items-center justify-between">
                      <div className="text-base text-gray-600">
                        Complete all required fields and submit your responses for review
                      </div>
                      <button
                        onClick={submitMyArea}
                        className="px-10 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-base font-semibold transition-all duration-200 flex items-center shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                      >
                        <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                        </svg>
                        Submit Responses
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <ScopeAssignModal
        isOpen={showScopeModal}
        onClose={() => setShowScopeModal(false)}
        assessmentId={id}
        initialDepartments={assessment.departments || []}
        onSaved={() => {
          fetchAssessmentDetail();
          setActiveTab('scope');
          setMessage('✅ Scope & assignments updated');
          setTimeout(() => setMessage(''), 3000);
        }}
      />

      <ExceptionsModal
        isOpen={showExceptions}
        onClose={()=>setShowExceptions(false)}
        assessmentId={id}
        domainId={flagContext?.domain_id || null}
        departmentId={flagContext?.department_id || null}
      />

      {showBoardPack && (
        <div className="fixed inset-0 z-50 bg-white">
          <BoardPack assessmentId={id} onBack={()=>setShowBoardPack(false)} />
        </div>
      )}

      {/* Evidence Upload Modal */}
      <EvidenceUploadModal
        isOpen={showEvidenceModal}
        onClose={() => setShowEvidenceModal(false)}
        assessmentId={id}
        onSuccess={(message) => {
          setMessage(`✅ ${message}`);
          fetchEvidence();
          setTimeout(() => setMessage(''), 3000);
        }}
      />

      {/* Evidence Display Section */}
      {assessment?.status === 'approved' && requiresCEOApproval && ceoApprovalMethod === 'evidence' && (
        <div className="mt-6">
          <RiskCard>
            <RiskCardHeader 
              title="Senior Executive Approval Evidence" 
              subtitle="Upload evidence of senior executive approval to proceed to final publication"
            />
            <RiskCardBody>
              <EvidenceDisplay 
                evidence={evidence}
                onReview={handleEvidenceReview}
                canReview={allowApprove}
              />
            </RiskCardBody>
          </RiskCard>
        </div>
      )}
    </div>
  );
}
